/**
 * NegotiationTimeline
 * Visual timeline showing the full offer/counter-offer history for a quote request.
 * Reused by both client and contractor/verifier views.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  AttachMoney,
  SwapHoriz,
  CheckCircle,
  Cancel,
  Schedule,
  Person
} from '@mui/icons-material';
import api from '../../services/api';
import { brand, bg, border } from '../../theme/colors';

const actionConfig = {
  initial_response: {
    label: 'Initial Quote',
    color: brand.primary,
    bgColor: '#f3f0ff',
    borderColor: '#e0d7ff',
    icon: <AttachMoney fontSize="small" />
  },
  counter_offer: {
    label: 'Counter-Offer',
    color: '#f59e0b',
    bgColor: '#fffbeb',
    borderColor: '#fde68a',
    icon: <SwapHoriz fontSize="small" />
  },
  accept: {
    label: 'Accepted',
    color: '#10b981',
    bgColor: '#f0fdf4',
    borderColor: '#bbf7d0',
    icon: <CheckCircle fontSize="small" />
  },
  decline: {
    label: 'Declined',
    color: '#ef4444',
    bgColor: '#fef2f2',
    borderColor: '#fecaca',
    icon: <Cancel fontSize="small" />
  }
};

const roleColors = {
  client: '#2196f3',
  contractor: brand.primary,
  verifier: brand.secondary
};

const NegotiationTimeline = ({ quoteRequestId, maxRounds = 5, currentRound = 0 }) => {
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (quoteRequestId) {
      loadNegotiations();
    }
  }, [quoteRequestId]);

  const loadNegotiations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/quote-requests/${quoteRequestId}/negotiations`);
      setNegotiations(response.data.data?.negotiations || []);
    } catch (err) {
      console.error('Error loading negotiations:', err);
      setError('Failed to load negotiation history');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (negotiations.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No negotiation history yet.
      </Alert>
    );
  }

  const roundProgress = (currentRound / maxRounds) * 100;

  return (
    <Box>
      {/* Round Progress */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: bg.subtle,
          border: `1px solid ${border.default}`,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Negotiation Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Round {currentRound} of {maxRounds}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={roundProgress}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: border.default,
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: currentRound >= maxRounds ? '#ef4444' : brand.primary
            }
          }}
        />
        {currentRound >= maxRounds && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            Maximum rounds reached. Please accept or decline.
          </Typography>
        )}
      </Paper>

      {/* Timeline */}
      <Box sx={{ position: 'relative' }}>
        {/* Vertical line */}
        <Box
          sx={{
            position: 'absolute',
            left: 20,
            top: 0,
            bottom: 0,
            width: 2,
            bgcolor: border.default
          }}
        />

        {negotiations.map((negotiation, index) => {
          const config = actionConfig[negotiation.action] || actionConfig.counter_offer;
          const roleColor = roleColors[negotiation.offered_by_role] || '#666';

          return (
            <Box key={negotiation.id || index} sx={{ display: 'flex', mb: 3, position: 'relative' }}>
              {/* Timeline dot */}
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: config.color,
                  zIndex: 1,
                  flexShrink: 0
                }}
              >
                {config.icon}
              </Avatar>

              {/* Content */}
              <Paper
                elevation={0}
                sx={{
                  ml: 2,
                  p: 2,
                  flex: 1,
                  bgcolor: config.bgColor,
                  border: `1px solid ${config.borderColor}`,
                  borderRadius: 2
                }}
              >
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{
                        bgcolor: config.color,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22
                      }}
                    />
                    <Chip
                      label={negotiation.offered_by_role?.charAt(0).toUpperCase() + negotiation.offered_by_role?.slice(1)}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: roleColor,
                        color: roleColor,
                        fontSize: '0.65rem',
                        height: 20
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Round {negotiation.round_number}
                  </Typography>
                </Box>

                {/* Amount */}
                {negotiation.action !== 'decline' && (
                  <Typography variant="h6" fontWeight={700} sx={{ color: config.color, mb: 0.5 }}>
                    {formatCurrency(negotiation.amount, negotiation.currency)}
                  </Typography>
                )}

                {/* Details */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: negotiation.message ? 1 : 0 }}>
                  {negotiation.offered_by_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {negotiation.offered_by_name}
                      </Typography>
                    </Box>
                  )}
                  {negotiation.proposed_start_date && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Schedule sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Start: {new Date(negotiation.proposed_start_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                  {negotiation.estimated_timeline_days && (
                    <Typography variant="caption" color="text.secondary">
                      {negotiation.estimated_timeline_days} days
                    </Typography>
                  )}
                </Box>

                {/* Message */}
                {negotiation.message && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    "{negotiation.message}"
                  </Typography>
                )}

                {/* Date */}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                  {formatDate(negotiation.created_at)}
                </Typography>
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default NegotiationTimeline;
